<template>
  <v-dialog
    v-model="dialog"
    max-width="450"
    @click:outside="close"
    :persistent="loading"
  >
    <v-card>
      <v-card-title>
        <span class="text-h5">Nova conta bancária</span>
      </v-card-title>
      <v-card-text>
        <v-card
          v-if="organization"
          outlined
          class="pa-2 rounded-lg mb-4 d-flex align-center"
        >
          <VAvatar size="40" class="mr-2">
            <VImg :src="organization.photo" />
          </VAvatar>
          <div class="d-flex flex-column">
            <h6 class="mb-0">{{ organization.name }}</h6>
            <span class="text-body-2">{{ organization.document }}</span>
          </div>
        </v-card>

        <v-form v-model="valid" @submit.prevent>
          <v-btn-toggle
            v-model="documentType"
            class="d-flex mb-4"
            color="primary"
            mandatory
          >
            <v-btn value="CPF" class="flex-1" small>CPF</v-btn>
            <v-btn value="CNPJ" class="flex-1" small>CNPJ</v-btn>
          </v-btn-toggle>

          <v-text-field-simplemask
            v-model="data.document"
            :label="`${documentType} do titular`"
            v-bind:properties="{
              outlined: true,
              placeholder:
                documentType === 'CPF'
                  ? '###.###.###-##'
                  : '##.###.###/####-##',
              type: 'tel',
              rules: [
                (value) => !!value || 'Documento é obrigatório',
                (value) =>
                  documentType === 'CPF'
                    ? validateCpf(value)
                    : validateCnpj(value) || 'Documento inválido',
              ],
              disabled: loading,
              dense: true,
            }"
            v-bind:options="{
              inputMask:
                documentType === 'CPF'
                  ? '###.###.###-##'
                  : '##.###.###/####-##',
              outputMask:
                documentType === 'CPF'
                  ? '###.###.###-##'
                  : '##.###.###/####-##',
              applyAfter: false,
              alphanumeric: false,
              lowerCase: false,
            }"
          />

          <v-text-field
            v-model="data.beneficiary"
            label="Beneficiário"
            outlined
            dense
            :rules="[(value) => !!value || 'Beneficiário é obrigatório']"
          />

          <div class="d-flex gap-2 mt-2">
            <v-select
              v-model="data.pixKeyType"
              :items="keyTypes"
              label="Tipo de chave pix"
              class="flex-1"
              outlined
              dense
              hide-details
            />

            <div style="flex: 2">
              <v-text-field-simplemask
                v-if="data.pixKeyType === 'CNPJ'"
                v-model="data.pixKey"
                :label="'Chave pix (CNPJ)'"
                v-bind:properties="{
                  outlined: true,
                  placeholder: '##.###.###/####-##',
                  type: 'tel',
                  rules: [
                    (value) => !!value || 'CNPJ é obrigatório',
                    (value) => validateCnpj(value) || 'CNPJ inválido',
                  ],
                  disabled: loading,
                  dense: true,
                }"
                v-bind:options="{
                  inputMask: '##.###.###/####-##',
                  outputMask: '##.###.###/####-##',
                  applyAfter: false,
                  alphanumeric: false,
                  lowerCase: false,
                }"
              />
              <v-text-field-simplemask
                v-if="data.pixKeyType === 'CPF'"
                v-model="data.pixKey"
                :label="'Chave pix (CFP)'"
                v-bind:properties="{
                  outlined: true,
                  placeholder: '###.###.###-##',
                  type: 'tel',
                  rules: [
                    (value) => !!value || 'CPF é obrigatório',
                    (value) => validateCpf(value) || 'CPF inválido',
                  ],
                  disabled: loading,
                  dense: true,
                }"
                v-bind:options="{
                  inputMask: '###.###.###-##',
                  outputMask: '###.###.###-##',
                  applyAfter: false,
                  alphanumeric: false,
                  lowerCase: false,
                }"
              />
              <v-text-field-simplemask
                v-if="data.pixKeyType === 'PHONE'"
                v-model="data.pixKey"
                :label="'Chave pix (Telefone)'"
                v-bind:properties="{
                  outlined: true,
                  placeholder: '(##) #####-####',
                  type: 'tel',
                  rules: [
                    (v) => !!v || 'Telefone é obrigatório',
                    (v) => v.length === 15 || 'Telefone inválido',
                  ],
                  disabled: loading,
                  dense: true,
                }"
                v-bind:options="{
                  inputMask: '(##) #####-####',
                  outputMask: '(##) #####-####',
                  applyAfter: false,
                  alphanumeric: false,
                  lowerCase: false,
                }"
              />
              <v-text-field
                v-else-if="data.pixKeyType === 'EMAIL'"
                v-model="data.pixKey"
                label="Chave pix (Email)"
                class="flex-1"
                type="email"
                outlined
                dense
                :rules="[
                  (value) => !!value || 'Email é obrigatório',
                  (value) => validateEmail(value) || 'Email inválido',
                ]"
              />

              <v-text-field
                v-if="data.pixKeyType === 'ID'"
                v-model="data.pixKey"
                label="Chave pix (ID)"
                type="text"
                outlined
                dense
                :rules="[(value) => !!value || 'ID é obrigatório']"
              />
            </div>
          </div>

          <v-row class="mt-2">
            <v-col class="py-0">
              <v-text-field
                v-model="data.bank"
                label="Banco"
                outlined
                placeholder="Nome do banco"
                dense
                :rules="[(value) => !!value || 'Banco é obrigatório']"
              />
            </v-col>
            <v-col class="py-0">
              <v-text-field
                v-model="data.agency"
                label="Agência"
                outlined
                dense
                hint="Opcional"
              />
            </v-col>
            <v-col class="py-0">
              <v-text-field
                v-model="data.account"
                label="Conta"
                outlined
                dense
                hint="Opcional"
              />
            </v-col>
          </v-row>
        </v-form>
        <v-alert v-if="error" type="error" class="mb-0">
          {{ error }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-btn text @click="close" :disabled="loading"> Cancelar </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          @click="save"
          :disabled="!valid"
          :loading="loading"
        >
          Salvar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ACCOUNT from "@/services/account";
import ORGANIZATION from "@/services/admin/organizations";

import validateCpf from "@/utils/validate-cpf";
import validateCnpj from "@/utils/validate-cnpj";

const defaultData = () => {
  return {
    document: "",
    bank: "",
    agency: "",
    account: "",
    pixKey: "",
    pixKeyType: "ID",
  };
};

export default {
  props: {
    organization: {
      type: Object,
    },
  },
  data() {
    return {
      dialog: false,
      loading: false,
      error: null,
      valid: false,
      data: defaultData(),
      documentType: "CPF",
      keyTypes: [
        { text: "ID", value: "ID" },
        { text: "CPF", value: "CPF" },
        { text: "CNPJ", value: "CNPJ" },
        { text: "Telefone", value: "PHONE" },
        { text: "Email", value: "EMAIL" },
      ],
    };
  },
  methods: {
    validateCpf: validateCpf,
    validateCnpj: validateCnpj,
    validateEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    },
    open() {
      this.dialog = true;
      this.data = defaultData();
    },
    close() {
      this.dialog = false;
    },
    async save() {
      this.loading = true;
      try {
        const response = await this.performSave();
        this.$emit("success", response);
        this.close();
      } catch (error) {
        this.error = error.message || "Erro ao salvar conta bancária";
      } finally {
        this.loading = false;
      }
    },
    performSave() {
      if (this.organization)
        return ORGANIZATION.createBankAccount(this.organization.id, this.data);

      return ACCOUNT.bankAccount.create(this.data);
    },
  },
  mounted() {
    this.$root.$on("add-bank-account", this.open);
  },
};
</script>
